// SIPP-729: Story Mode Screen 4 — Utility Snapshot (Figma redesign)
import { Box, Divider, Stack, Typography, useTheme } from '@mui/material'
import { useSelector } from 'react-redux'
import { fNumber } from '../../../utils/formatNumber'
import { ownerTypeCodeToLong, primSrcCdToLong } from '../../../utils/SdwaUtils'
import { PubWaterSystemSelector } from '../../../store/publicWaterSystem/pubWaterSystem'
import { ViolationEnforcementSelector } from '../../../store/ViolationEnforcements/violationEnforcement.selector'
import { WaterQualityIndexSelectors } from '../../../store/waterQualityIndex'

export const StoryScreen4 = () => {
  const theme = useTheme()

  // Utility data
  const wqiUtility = useSelector(WaterQualityIndexSelectors.selectUtility)
  const pwsSummary = useSelector(PubWaterSystemSelector.makeObjectSummary) as any
  const pwsData = useSelector(PubWaterSystemSelector.data) as any

  // Violations
  const violations = useSelector(ViolationEnforcementSelector.groupedEnfByViolation.data)
  const totalViolations = violations?.length ?? 0

  // Derive display values
  const rawUtilityName = wqiUtility?.name || pwsSummary?.name || ''
  const city = pwsData?.CITY_NAME || ''
  const stateCode = pwsData?.STATE_CODE || ''
  const location = city && stateCode ? `${city}, ${stateCode}` : city || stateCode || ''
  const popServed = pwsSummary?.pop_served
  const ownerType = ownerTypeCodeToLong(pwsSummary?.owner_type || '')
  const primSource = primSrcCdToLong(pwsSummary?.prim_source || '')

  const isWell = !rawUtilityName && !popServed && !ownerType && !primSource

  if (isWell) {
    return (
      <Box sx={{ p: 3, maxWidth: 600, mx: 'auto', width: '100%' }}>
        <Typography
          sx={{ textAlign: 'center', color: 'text.secondary', fontSize: 14, mt: 4 }}
        >
          🪣 You're on a private well. Public utility data isn't available for your water source.
        </Typography>
      </Box>
    )
  }

  const fallbackName =
    city && stateCode
      ? `Public Water Utility — ${city}, ${stateCode}`
      : 'Public Water Utility'
  const displayName = rawUtilityName || fallbackName

  const amberColor = theme.palette.warning.main
  const amberBg = theme.palette.warning.light

  const rows = [
    { emoji: '📍', label: 'Location', value: location },
    { emoji: '👥', label: 'Population Served', value: popServed ? fNumber(popServed) : '' },
    { emoji: '🏛️', label: 'Ownership', value: ownerType },
    { emoji: '💧', label: 'Water Source', value: primSource },
  ].filter((row) => !!row.value)

  return (
    <Box sx={{ p: 3, maxWidth: 600, mx: 'auto', width: '100%' }}>
      {/* Title */}
      <Typography sx={{ fontWeight: 700, fontSize: 22, mb: 0.25 }}>
        {displayName}
      </Typography>

      {/* Subtitle */}
      <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'text.secondary', mb: 2.5 }}>
        Your Public Water Utility
      </Typography>

      {/* Info card */}
      <Box
        sx={{
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: '10px',
          p: 2,
        }}
      >
        {rows.map((row, idx) => (
          <Box key={row.label}>
            {idx > 0 && (
              <Divider sx={{ borderColor: theme.palette.grey[200], my: 1.5 }} />
            )}
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography sx={{ fontSize: 18, lineHeight: 1 }}>{row.emoji}</Typography>
              <Stack>
                <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.3 }}>
                  {row.label}
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.4 }}>
                  {row.value}
                </Typography>
              </Stack>
            </Stack>
          </Box>
        ))}

        {/* Violations row */}
        <Divider sx={{ borderColor: theme.palette.grey[200], my: 1.5 }} />
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography sx={{ fontSize: 18, lineHeight: 1 }}>⚠️</Typography>
            <Stack>
              <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.3 }}>
                Total Violations
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.4 }}>
                {totalViolations} on record
              </Typography>
            </Stack>
          </Stack>

          {/* Review pill */}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              bgcolor: amberBg,
              borderRadius: '999px',
              px: 1.5,
              py: 0.5,
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: amberColor,
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: amberColor,
                lineHeight: 1,
              }}
            >
              Review
            </Typography>
          </Box>
        </Stack>
      </Box>
    </Box>
  )
}
